/**
 * @fileoverview
 * Confirm Dialog Component - Reusable confirmation dialog
 * Modern design with smooth animations and type-based styling
 *
 * @version 2.0.0
 * @author KATALYST TEAM
 */

import {
  Component,
  Input,
  Output,
  EventEmitter,
  HostListener,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';

export type ConfirmDialogType = 'warning' | 'danger' | 'info' | 'success';

@Component({
  selector: 'app-confirm-dialog',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './confirm-dialog.component.html',
  styleUrl: './confirm-dialog.component.css',
})
export class ConfirmDialogComponent {
  @Input() isOpen: boolean = false;
  @Input() title: string = 'Confirm';
  @Input() message: string = 'Are you sure you want to proceed?';
  @Input() confirmText: string = 'Confirm';
  @Input() cancelText: string = 'Cancel';
  @Input() type: ConfirmDialogType = 'warning';
  @Input() closeOnBackdropClick: boolean = true;
  @Input() closeOnEscape: boolean = true;
  @Input() showCloseButton: boolean = true;
  @Input() loading: boolean = false;

  @Output() onConfirm = new EventEmitter<void>();
  @Output() onCancel = new EventEmitter<void>();

  @HostListener('document:keydown.escape')
  handleEscapeKey() {
    if (this.closeOnEscape && this.isOpen && !this.loading) {
      this.cancel();
    }
  }

  confirm() {
    if (!this.loading) {
      this.onConfirm.emit();
    }
  }

  cancel() {
    if (!this.loading) {
      this.onCancel.emit();
    }
  }

  onBackdropClick(event: Event) {
    if (this.closeOnBackdropClick && !this.loading) {
      this.cancel();
    }
  }

  get iconContainerClass(): string {
    const baseClass = 'flex h-14 w-14 items-center justify-center rounded-full';
    switch (this.type) {
      case 'danger':
        return `${baseClass} bg-red-50`;
      case 'success':
        return `${baseClass} bg-green-50`;
      case 'info':
        return `${baseClass} bg-blue-50`;
      case 'warning':
      default:
        return `${baseClass} bg-amber-50`;
    }
  }

  get iconClass(): string {
    switch (this.type) {
      case 'danger':
        return 'text-red-500';
      case 'success':
        return 'text-green-500';
      case 'info':
        return 'text-blue-500';
      case 'warning':
      default:
        return 'text-amber-500';
    }
  }

  get confirmButtonClass(): string {
    const baseClass = 'inline-flex items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';
    switch (this.type) {
      case 'danger':
        return `${baseClass} bg-red-600 hover:bg-red-700 focus:ring-red-500`;
      case 'success':
        return `${baseClass} bg-green-600 hover:bg-green-700 focus:ring-green-500`;
      case 'info':
        return `${baseClass} bg-blue-600 hover:bg-blue-700 focus:ring-blue-500`;
      case 'warning':
      default:
        return `${baseClass} bg-[#6729FF] hover:bg-[#5521D9] focus:ring-[#6729FF]`;
    }
  }

  get cancelButtonClass(): string {
    return 'inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 shadow-sm transition-all duration-200 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';
  }
}
